import { NextResponse } from "next/server"
import {
  getSession,
  parseWordPressLoggedInCookie,
  setSession,
} from "@/services/auth-service"
import { getWooCommerceCustomer } from "@/services/woo-commerce-service"

const NO_CACHE_HEADERS = {
  "Cache-Control": "no-store",
} as const

/**
 * Determines if user data should be refreshed from WooCommerce.
 * Returns true when:
 * - Session has no user data
 * - Session user email mismatches WordPress logged-in identity
 */
const shouldRefreshUser = (
  session: NonNullable<Awaited<ReturnType<typeof getSession>>>,
  wpUsername: string | null
): boolean => {
  if (!session.user) {
    return true
  }

  if (wpUsername && session.user.email !== wpUsername) {
    return true
  }

  return false
}

export const GET = async () => {
  const session = await getSession()

  if (!session) {
    return NextResponse.json(
      {
        ok: false,
        code: "UNAUTHORIZED",
        message: "Sessao nao autenticada.",
      },
      {
        status: 401,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  const wpIdentity = parseWordPressLoggedInCookie(
    session.wordpressSessionCookie
  )
  const wpUsername = wpIdentity?.username ?? null

  if (shouldRefreshUser(session, wpUsername)) {
    if (!wpUsername) {
      return NextResponse.json(
        {
          ok: false,
          code: "UNAUTHORIZED",
          message: "Sessao nao autenticada.",
        },
        {
          status: 401,
          headers: NO_CACHE_HEADERS,
        }
      )
    }

    const freshUser = await getWooCommerceCustomer(wpUsername)

    if (freshUser) {
      await setSession({
        wordpressSessionCookie: session.wordpressSessionCookie,
        user: freshUser,
      })

      return NextResponse.json(
        {
          ok: true,
          user: freshUser,
        },
        { headers: NO_CACHE_HEADERS }
      )
    }

    if (session.user) {
      return NextResponse.json(
        {
          ok: true,
          user: session.user,
        },
        { headers: NO_CACHE_HEADERS }
      )
    }

    return NextResponse.json(
      {
        ok: false,
        code: "UNAUTHORIZED",
        message: "Sessao nao autenticada.",
      },
      {
        status: 401,
        headers: NO_CACHE_HEADERS,
      }
    )
  }

  return NextResponse.json(
    {
      ok: true,
      user: session.user,
    },
    { headers: NO_CACHE_HEADERS }
  )
}
